import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext({ toggleTheme: () => {} });

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedTheme = sessionStorage.getItem("theme");
    return (savedTheme as "light" | "dark") || "light";
  });

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      sessionStorage.setItem("theme", newMode);
      return newMode;
    });
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === "light" ? "#FF6844" : "#031829",
            contrastText: "#fff",
          },
          background: {
            default: mode === "light" ? "#ffffff" : "#121212",
            paper: mode === "light" ? "#ffffff" : "#1A1D21",
          },
          text: {
            primary: mode === "light" ? "#0F202B" : "#ffffff",
            secondary: mode === "light" ? "#142C3C" : "#E0E0E0",
          },
          secondary: {
            main: mode === "light" ? "#fb8b0e" : "#fb8b0e",
            contrastText: "#fff",
          },
          divider: mode === "light" ? "#D6D6D6" : "#333333",
          action: {
            hover:
              mode === "light"
                ? "rgba(0, 0, 0, 0.04)"
                : "rgba(255, 255, 255, 0.08)",
          },
        },
        typography: {
          fontFamily: "Poppins, Arial, sans-serif",
          h1: {
            color: mode === "light" ? "#000" : "#fff",
          },
          body1: {
            color: mode === "light" ? "#000" : "#fff",
          },
          body2: {
            color: mode === "light" ? "#000" : "#fff",
            fontSize: "13px",
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                "--primary-color": mode === "light" ? "#034ea2" : "#031829",
                "--profile-text-color": mode === "light" ? "#404040" : "#fff",
                "--profile-role-color": mode === "light" ? "#565656" : "#fff",
                "--menu-item-color": mode === "light" ? "#737373" : "#fff",
                "--notification-background-color":
                  mode === "light" ? "#f5f5f5" : "#F5F5F5",
                "--content-text-color":mode==="light"?"#1B281B":
                "#1B281B",
                "--background-color": mode === "light" ? "#ffffff" : "#1A1D21",
                "--text-color": mode === "light" ? "#000000" : "#ffffff",
                "--footer-top-color": mode === "light" ? "#044FA2" : "#161616",
                "--footer-bottom-color":
                  mode === "light" ? "#0C4686" : "#000000",
                "--icon-background-color":
                  mode === "light" ? "#fff" : "#565656",
                "--icon-shadow": mode === "light" ? "#BFBFBF40" : "#333",
                "--faq-background-color": mode === "light" ? "#fff" : "#171E25",
                "--slider-border":
                  mode === "light" ? "1px solid #D6D6D6" : "none",
                "--accordin-separator":
                  mode === "light" ? "1px solid #E9E9E9" : "1px solid #5C5C5C",
                "--accordin-sub-header-color":
                  mode === "light" ? "#449CFD" : "#A7D1FF",
                "--web-banner-gradient":
                  mode === "light"
                    ? "linear-gradient(to right, #002b80, #679cd4)"
                    : "linear-gradient(to right,rgb(25, 25, 26),rgb(68, 68, 68))",
                "--home-banner-background":
                  mode === "light" ? "#dff9ee" : "#0d1915",
                "--home-banner-overlay":
                  mode === "light"
                    ? "linear-gradient(transparent, transparent)"
                    : "linear-gradient(rgba(5, 15, 12, 0.58), rgba(5, 15, 12, 0.72))",
                "--home-banner-text":
                  mode === "light" ? "#17312a" : "#f2fff9",
                "--home-banner-surface":
                  mode === "light"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(18, 34, 29, 0.92)",
                "--home-banner-surface-border":
                  mode === "light"
                    ? "rgba(255, 255, 255, 0.55)"
                    : "rgba(181, 226, 207, 0.24)",
                "--home-banner-shadow":
                  mode === "light"
                    ? "0 12px 32px rgba(25, 89, 66, 0.08)"
                    : "0 14px 38px rgba(0, 0, 0, 0.4)",
                "--home-banner-accent":
                  mode === "light" ? "#293d90" : "#aebcff",
                "--home-banner-card":
                  mode === "light" ? "#d8eadf" : "#162a23",
                "--home-banner-control":
                  mode === "light" ? "#244c3d" : "#e7fff5",
                "--home-banner-control-border":
                  mode === "light" ? "#2c5646" : "#83b8a3",
                "--home-banner-control-bg":
                  mode === "light"
                    ? "rgba(255, 255, 255, 0.45)"
                    : "rgba(12, 28, 23, 0.78)",
                "--home-banner-control-hover":
                  mode === "light" ? "#ffffff" : "#294b3f",
                "--home-banner-dot":
                  mode === "light" ? "#85958e" : "#9bada6",
                "--home-banner-dot-active":
                  mode === "light" ? "#106426" : "#72e3a4",
                "--how-it-works-overlay":
                  mode === "light"
                    ? "rgba(246, 255, 249, 0.08)"
                    : "rgba(8, 18, 14, 0.94)",
                "--how-it-works-background":
                  mode === "light" ? "#f5fff8" : "#08120e",
                "--how-it-works-text":
                  mode === "light" ? "#101814" : "#effbf4",
                "--how-it-works-muted":
                  mode === "light" ? "#33483d" : "#c5d8ce",
                "--how-it-works-number":
                  mode === "light" ? "#d5c9a5" : "#e4d7ad",
                "--how-it-works-line":
                  mode === "light"
                    ? "rgba(39, 70, 56, 0.55)"
                    : "rgba(174, 218, 197, 0.58)",
                "--how-it-works-marker":
                  mode === "light" ? "#185c31" : "#70d493",
                "--how-it-works-card":
                  mode === "light" ? "#dce9df" : "#13271e",
                "--how-it-works-card-border":
                  mode === "light"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(132, 207, 171, 0.28)",
                "--how-it-works-shadow":
                  mode === "light"
                    ? "0 14px 28px rgba(34, 73, 53, 0.13)"
                    : "0 18px 34px rgba(0, 0, 0, 0.46)",
                "--how-it-works-image-filter":
                  mode === "light"
                    ? "none"
                    : "brightness(0.8) saturate(0.82) contrast(1.08)",
                "--home-video-background":
                  mode === "light" ? "#f7fff9" : "#08120e",
                "--home-video-pattern-overlay":
                  mode === "light"
                    ? "rgba(220, 255, 230, 0.38)"
                    : "rgba(10, 27, 18, 0.78)",
                "--home-video-text":
                  mode === "light" ? "#173d31" : "#effbf4",
                "--home-video-surface":
                  mode === "light" ? "#d7e5da" : "#13271e",
                "--home-video-border":
                  mode === "light"
                    ? "rgba(24, 91, 54, 0.14)"
                    : "rgba(132, 207, 171, 0.28)",
                "--home-video-shadow":
                  mode === "light"
                    ? "0 16px 42px rgba(27, 72, 48, 0.14)"
                    : "0 20px 48px rgba(0, 0, 0, 0.48)",
                "--home-video-play":
                  mode === "light" ? "#3c9d50" : "#56b96f",
                "--home-video-play-hover":
                  mode === "light" ? "#2d873f" : "#6bce83",
                "--home-video-focus":
                  mode === "light" ? "#4caf6a" : "#8ae6a0",
                "--app-download-background":
                  mode === "light" ? "#e8f8ea" : "#0b1c14",
                "--app-download-wedge":
                  mode === "light" ? "#f8fff9" : "#07110d",
                "--app-download-text":
                  mode === "light" ? "#142b35" : "#effbf4",
                "--app-download-muted":
                  mode === "light" ? "#263f3b" : "#c4d8ce",
                "--app-download-letter":
                  mode === "light"
                    ? "rgba(76, 151, 91, 0.12)"
                    : "rgba(115, 213, 145, 0.1)",
                "--app-download-qr-border":
                  mode === "light" ? "#ffffff" : "#cce9da",
                "--app-download-focus":
                  mode === "light" ? "#21773c" : "#82dfa0",
                "--app-download-store-background":
                  mode === "light" ? "#1A5F20" : "#111111",
                "--form-background": mode === "light" ? "#fff" : "#1e1e1e",
                "--form-text": mode === "light" ? "#0F202B" : "#ffffff",
                "--form-border": mode === "light" ? "#E6F4FC" : "#333333",
                "--form-label": "#B8B8B8",
                "--form-error": mode === "light" ? "#d32f2f" : "#f44336",
                "--form-disabled":
                  mode === "light"
                    ? "rgba(0, 0, 0, 0.38)"
                    : "rgba(255, 255, 255, 0.38)",
                "--form-hover":
                  mode === "light"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "rgba(255, 255, 255, 0.08)",
                "--form-focus": mode === "light" ? "#0F202BE5" : "#ffffff",
                "--form-shadow":
                  mode === "light"
                    ? "0 4px 24px 0 rgba(44, 62, 80, 0.08)"
                    : "0 4px 24px 0 rgba(0, 0, 0, 0.2)",
                "--form-placeholder": mode === "light" ? "#B8B8B8" : "#A0A0A0",
                "--form-success": mode === "light" ? "#4caf50" : "#66bb6a",
                "--form-warning": mode === "light" ? "#ff9800" : "#ffa726",
                "--form-info": mode === "light" ? "#2196f3" : "#29b6f6",
                "--home-header-color":mode==="light"?"0f202b":"#fff"
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  color: "#B8B8B8 !important",
                  "&.Mui-focused, &.MuiInputLabel-shrink, &.Mui-disabled": {
                    color: "#B8B8B8 !important",
                  },
                }),
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                "& .MuiOutlinedInput-root": {
                  backgroundColor: mode === "light" ? "#fff" : "#1e1e1e",
                  "& fieldset": {
                    borderColor: mode === "light" ? "#E6F4FC" : "#333333",
                  },
                  "&:hover fieldset": {
                    borderColor: mode === "light" ? "#0F202BE5" : "#ffffff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: mode === "light" ? "#0F202BE5" : "#ffffff",
                  },
                },
                "& .MuiInputLabel-root": {
                  color:
                    mode === "dark"
                      ? "#B8B8B8 !important"
                      : "#B8B8B8",
                  "&.Mui-focused": {
                    color:
                      mode === "light"
                        ? "#0F202BE5"
                        : "#B8B8B8 !important",
                  },
                  "&.MuiInputLabel-shrink, &.Mui-disabled": {
                    color:
                      mode === "dark"
                        ? "#B8B8B8 !important"
                        : "#B8B8B8",
                  },
                },
              },
            },
          },
          MuiModal: {
            defaultProps: {
              disableScrollLock: true,
            },
          },
          MuiDialog: {
            defaultProps: {
              disableScrollLock: true,
            },
          },
          MuiMenu: {
            defaultProps: {
              disableScrollLock: true,
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "light" ? "#fff" : "#1e1e1e",
                color: mode === "light" ? "#0F202B" : "#ffffff",
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "light" ? "#2E3B80" : "#031829",
                color: "#fff",
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
